import React, { useState, useRef, useEffect, useCallback } from 'react';
import { MessageCircle, Send, Loader2, AlertCircle, Cpu, Gauge, RotateCcw, FileText, FileSpreadsheet, Download } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkMath from 'remark-math';
import remarkGfm from 'remark-gfm';
import rehypeKatex from 'rehype-katex';
import 'katex/dist/katex.min.css';

const MAX_CHAT_HISTORY = 6;

export const GEMINI_CHAT_MODELS = [
  {
    id: 'gemini-3.1-flash-lite',
    label: 'Express',
    description: 'Schnelle Antwort für die meisten Fragen',
  },
  {
    id: 'gemini-3.1-pro-preview',
    label: 'Mit Tiefgang',
    description: 'Gründliche Analyse für komplexe Fragen',
  },
];

// Generated files come back with a server-relative download_url (e.g. /api/docs/files/...);
// resolve it against the chat endpoint's origin so downloads hit the right backend.
const resolveDownloadHref = (chatUrl, downloadUrl) => {
  try {
    return new URL(downloadUrl, new URL(chatUrl, window.location.origin)).toString();
  } catch {
    return downloadUrl;
  }
};

const isSpreadsheet = (file) => /\.xlsx$/i.test(file.name || '') || (file.mime || '').includes('spreadsheet');

const GeneratedFileCard = ({ file, chatUrl }) => {
  const Icon = isSpreadsheet(file) ? FileSpreadsheet : FileText;
  const iconColor = isSpreadsheet(file) ? 'text-emerald-600' : 'text-blue-600';
  return (
    <a
      href={resolveDownloadHref(chatUrl, file.download_url)}
      download={file.name}
      className="flex items-center gap-2.5 bg-green-50 border border-green-200 rounded-md px-3 py-2 hover:bg-green-100 transition-colors group no-underline"
    >
      <Icon className={`h-5 w-5 flex-shrink-0 ${iconColor}`} />
      <div className="min-w-0 flex-1">
        <p className="text-xs font-medium text-slate-800 truncate">{file.name}</p>
        <p className="text-[11px] text-slate-500">{(file.size / 1024).toFixed(0)} KB · Klicken zum Herunterladen</p>
      </div>
      <Download className="h-4 w-4 text-slate-400 group-hover:text-green-700 flex-shrink-0" />
    </a>
  );
};

const readStoredModel = (storageKey, fallback) => {
  if (!storageKey) return fallback;
  try {
    const v = window.localStorage.getItem(storageKey);
    return v || fallback;
  } catch {
    return fallback;
  }
};

export const ChatView = ({
  sessionId,
  sessionError,
  chatUrl,
  emptyHint = 'Frage stellen?',
  inputPlaceholder = 'Frage stellen...',
  height = 'h-full',
  availableModels,
  modelStorageKey = 'chatModelId',
  tokenWarningThreshold = 800_000,
  onResponse,
}) => {
  const showModelPicker = Array.isArray(availableModels) && availableModels.length > 1;
  const defaultModelId = showModelPicker ? availableModels[0].id : null;
  const initialModelId = showModelPicker
    ? (availableModels.find(m => m.id === readStoredModel(modelStorageKey, defaultModelId))?.id || defaultModelId)
    : null;

  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [selectedModel, setSelectedModel] = useState(initialModelId);
  const [totalTokens, setTotalTokens] = useState(0);
  const [lastUsage, setLastUsage] = useState(null);
  const [warningDismissed, setWarningDismissed] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  const showWarning = totalTokens >= tokenWarningThreshold && !warningDismissed;

  const resetChat = useCallback(() => {
    setMessages([]);
    setTotalTokens(0);
    setLastUsage(null);
    setWarningDismissed(false);
    setInput('');
    inputRef.current?.focus();
  }, []);

  const handleModelChange = useCallback((id) => {
    setSelectedModel(id);
    if (modelStorageKey) {
      try { window.localStorage.setItem(modelStorageKey, id); } catch { /* ignore */ }
    }
  }, [modelStorageKey]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const sendMessage = useCallback(async () => {
    const text = input.trim();
    if (!text || isLoading || !sessionId) return;

    const userMsg = { role: 'user', content: text };
    const newMessages = [...messages, userMsg];
    setMessages(newMessages);
    setInput('');
    setIsLoading(true);

    const recentMessages = newMessages.slice(-MAX_CHAT_HISTORY);

    try {
      const body = {
        messages: recentMessages.map(m => ({ role: m.role, content: m.content })),
        session_id: sessionId,
      };
      if (selectedModel) body.model = selectedModel;
      const response = await fetch(chatUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });

      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        throw new Error(errData.detail || `Server error ${response.status}`);
      }

      const data = await response.json();
      setMessages(prev => [...prev, { role: 'assistant', content: data.answer, files: data.files || [] }]);
      if (data.usage) {
        setLastUsage(data.usage);
        if (typeof data.usage.total_tokens === 'number') {
          setTotalTokens(prev => prev + data.usage.total_tokens);
        }
      }
      if (onResponse) onResponse(data);
    } catch (err) {
      setMessages(prev => [...prev, { role: 'assistant', content: `Fehler: ${err.message}`, isError: true }]);
    } finally {
      setIsLoading(false);
      inputRef.current?.focus();
    }
  }, [input, messages, isLoading, sessionId, chatUrl, selectedModel, onResponse]);

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const showHeader = showModelPicker || totalTokens > 0 || messages.length > 0;
  const tokenLabel = totalTokens >= 1000
    ? `~${(totalTokens / 1000).toFixed(totalTokens >= 10_000 ? 0 : 1)}k`
    : `${totalTokens}`;
  const tokenTooltipLines = lastUsage
    ? [
        `Letzte Antwort:`,
        `${lastUsage.prompt_tokens ?? '?'} Prompt + ${lastUsage.output_tokens ?? '?'} Output`,
        `= ${lastUsage.total_tokens ?? '?'} Tokens${lastUsage.cached_tokens ? ` · ${lastUsage.cached_tokens} gecacht` : ''}`,
      ]
    : ['Kumulierte Tokens in diesem Chat'];

  return (
    <div className={`flex flex-col ${height}`}>
      {showHeader && (
        <div className="flex items-center gap-3 px-4 py-2 border-b border-green-100 bg-white flex-shrink-0">
          {showModelPicker && (
            <>
              <Cpu className="h-3.5 w-3.5 text-gray-400" />
              <span className="text-xs text-gray-500">Eveline:</span>
              <div className="flex gap-1">
                {availableModels.map(m => (
                  <div key={m.id} className="relative group">
                    <button
                      type="button"
                      onClick={() => handleModelChange(m.id)}
                      disabled={isLoading}
                      aria-label={m.description || m.id}
                      className={`text-xs px-2.5 py-1 rounded transition-colors ${
                        selectedModel === m.id
                          ? 'bg-green-600 text-white'
                          : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      } disabled:opacity-50 disabled:cursor-not-allowed`}
                    >
                      {m.label}
                    </button>
                    {m.description && (
                      <div className="absolute left-1/2 top-full mt-2 -translate-x-1/2 z-20 opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity duration-150 delay-100">
                        <div className="relative bg-slate-800 text-white text-xs px-2.5 py-1.5 rounded shadow-lg whitespace-nowrap">
                          {m.description}
                          <div className="absolute -top-1 left-1/2 -translate-x-1/2 w-2 h-2 bg-slate-800 rotate-45" />
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </>
          )}
          <div className="ml-auto flex items-center gap-3">
            {totalTokens > 0 && (
              <div className="relative group">
                <div
                  className={`flex items-center gap-1.5 text-xs cursor-default ${
                    showWarning ? 'text-amber-700' : 'text-gray-500'
                  }`}
                >
                  <Gauge className="h-3.5 w-3.5" />
                  <span>{tokenLabel} Tokens</span>
                </div>
                <div className="absolute right-0 top-full mt-2 z-20 opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity duration-150 delay-100">
                  <div className="relative bg-slate-800 text-white text-xs px-2.5 py-1.5 rounded shadow-lg whitespace-nowrap leading-relaxed">
                    {tokenTooltipLines.map((line, i) => (
                      <div key={i}>{line}</div>
                    ))}
                    <div className="absolute -top-1 right-4 w-2 h-2 bg-slate-800 rotate-45" />
                  </div>
                </div>
              </div>
            )}
            {messages.length > 0 && (
              <button
                type="button"
                onClick={resetChat}
                disabled={isLoading}
                title="Chat zurücksetzen"
                className="flex items-center gap-1 text-xs text-gray-500 hover:text-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <RotateCcw className="h-3.5 w-3.5" />
                <span>Neuer Chat</span>
              </button>
            )}
          </div>
        </div>
      )}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {sessionError && (
          <div className="text-center text-red-500 mt-10">
            <AlertCircle className="h-10 w-10 mx-auto mb-2" />
            <p className="text-sm">Session konnte nicht erstellt werden: {sessionError}</p>
          </div>
        )}
        {!sessionError && messages.length === 0 && (
          <div className="text-center text-slate-400 mt-20">
            <MessageCircle className="h-12 w-12 mx-auto mb-3 opacity-50" />
            <p className="text-lg font-medium">{emptyHint}</p>
          </div>
        )}

        {messages.map((msg, i) => (
          <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
            <div className={`max-w-[75%] rounded-lg px-4 py-3 text-sm ${
              msg.role === 'user'
                ? 'bg-green-600 text-white'
                : msg.isError
                  ? 'bg-red-50 text-red-700 border border-red-200'
                  : 'bg-white text-slate-800 border border-green-100 shadow-sm'
            }`}>
              {msg.role === 'assistant' && !msg.isError ? (
                <>
                  <div className="prose prose-sm max-w-none prose-headings:text-slate-900 prose-headings:mt-3 prose-headings:mb-1 prose-p:my-1 prose-ul:my-1 prose-li:my-0.5 prose-strong:text-slate-900 prose-code:bg-green-50 prose-code:px-1 prose-code:rounded prose-code:text-green-700 prose-code:text-xs">
                    <ReactMarkdown remarkPlugins={[remarkGfm, remarkMath]} rehypePlugins={[rehypeKatex]}>{msg.content}</ReactMarkdown>
                  </div>
                  {msg.files?.length > 0 && (
                    <div className="mt-2.5 space-y-1.5">
                      {msg.files.map((f) => (
                        <GeneratedFileCard key={f.id} file={f} chatUrl={chatUrl} />
                      ))}
                    </div>
                  )}
                </>
              ) : (
                <p className="whitespace-pre-wrap leading-relaxed">{msg.content}</p>
              )}
            </div>
          </div>
        ))}

        {isLoading && (
          <div className="flex justify-start">
            <div className="bg-white border border-green-100 rounded-lg px-4 py-3 shadow-sm">
              <div className="flex items-center gap-2 text-sm text-slate-500">
                <Loader2 className="h-4 w-4 animate-spin" />
                <span>Analysiere...</span>
              </div>
            </div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      {showWarning && (
        <div className="border-t border-amber-200 bg-amber-50 px-4 py-2.5 flex items-start gap-2 flex-shrink-0">
          <AlertCircle className="h-4 w-4 text-amber-600 flex-shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0">
            <p className="text-xs text-amber-800">
              Der Chatverlauf verbraucht bereits ~{tokenLabel} Tokens. Ein neuer Chat hält Antworten schnell und günstig.
            </p>
          </div>
          <button
            type="button"
            onClick={resetChat}
            className="text-xs px-2.5 py-1 rounded bg-amber-600 text-white hover:bg-amber-700 flex-shrink-0"
          >
            Neuer Chat
          </button>
          <button
            type="button"
            onClick={() => setWarningDismissed(true)}
            className="text-xs text-amber-700 hover:text-amber-900 flex-shrink-0"
          >
            Ignorieren
          </button>
        </div>
      )}

      <div className="border-t border-green-100 p-4 bg-white">
        <div className="flex gap-2">
          <textarea
            ref={inputRef}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={inputPlaceholder}
            rows={1}
            className="flex-1 resize-none rounded-lg border border-slate-300 px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
          />
          <button
            onClick={sendMessage}
            disabled={!input.trim() || isLoading}
            className="px-4 py-2.5 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <Send className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default ChatView;
